import { useMemo } from 'react';

import type { PDFSource } from '@/modules/pdf/PDFViewer';

import { useDocumentDetailsQuery } from './useDocumentDetailsQuery';

/**
 * Fetches and derives all document-related data needed for the document details page.
 * Combines GraphQL query results with derived values like file URLs and pages.
 * Returns a memoized object to prevent unnecessary re-renders.
 */
export function useDocumentDetailsData(documentId: string) {
  const {
    document,
    loading,
    refetch: refetchQuery,
  } = useDocumentDetailsQuery(documentId);

  const file = document?.pdfFile ?? document?.file;
  const fileId = file?.id;

  const result = useMemo(() => {
    const householdId = document?.household?.id;
    const summaries = document?.summaries ?? [];
    const filePages = file?.pages ?? [];

    return {
      document,
      source: fileId ? ({ kind: 'file', fileId } satisfies PDFSource) : null,
      filePages,
      summaries,
      householdId,
      loading,
      refetch: refetchQuery,
    };
  }, [document, fileId, file?.pages, loading, refetchQuery]);

  return result;
}
